/**
 * Payment Modal Component
 * 
 * Handles three payment flows:
 * 1. Pay by Card (Stripe Elements integration)
 * 2. Pay by Cash (direct recording)
 * 3. Part Payment (split with method selection)
 * 
 * All amounts are rounded to 2 decimals.
 * Frontend stays dumb - backend handles ledger sync.
 */

import { useState, useEffect } from 'react';
import { supabase } from '../../../lib/supabase';
import { getAppUrl } from '../../../lib/url';
import { X, CreditCard, Banknote, Split, Loader2, CheckCircle, AlertCircle, ScanLine } from 'lucide-react';
import QRCode from 'react-qr-code';
import { DEFAULT_DOCUMENT_ACCENT } from '../../../lib/brandAccent';
import { strike } from '../../../lib/haptics';

type PaymentMode = 'card' | 'cash' | 'part';
type PaymentStatus = 'idle' | 'processing' | 'success' | 'error';

interface InvoiceData {
    id: string;
    org_id?: string;
    invoice_number: string;
    total_gross: number;
    balance_due: number;
    branding_snapshot: {
        business_name: string;
        invoice_accent_color: string;
    };
}

interface PaymentModalProps {
    invoice: InvoiceData | null;
    isOpen: boolean;
    onClose: () => void;
    onSuccess: () => void;
    initialMode?: PaymentMode;
}

export default function PaymentModal({ invoice, isOpen, onClose, onSuccess, initialMode }: PaymentModalProps) {
    const [mode, setMode] = useState<PaymentMode>(initialMode || 'card');
    const [status, setStatus] = useState<PaymentStatus>('idle');
    const [error, setError] = useState<string | null>(null);

    // Stripe connection status
    const [stripeConnected, setStripeConnected] = useState<boolean | null>(null);
    const [stripeVerified, setStripeVerified] = useState<boolean | null>(null);
    const [stripeCheckingStatus, setStripeCheckingStatus] = useState(false);

    // Part payment state
    const [partAmount, setPartAmount] = useState<string>('');
    const [partMethod, setPartMethod] = useState<'card' | 'cash'>('cash');
    const [promptEmailRemaining, setPromptEmailRemaining] = useState(false);
    const [emailSending, setEmailSending] = useState(false);

    // QR Code state
    const [checkoutUrl, setCheckoutUrl] = useState<string | null>(null);

    // Cash payment state
    const [cashReceived, setCashReceived] = useState<string>('');

    // Check Stripe connection and verification status when opening modal
    useEffect(() => {
        let isMounted = true;

        async function checkStripeStatus() {
            if (!invoice?.org_id) return;
            
            setStripeCheckingStatus(true);
            try {
                const { data, error } = await supabase
                    .from('organizations')
                    .select('stripe_account_id, stripe_verification_complete, stripe_charges_enabled')
                    .eq('id', invoice.org_id)
                    .single();

                if (isMounted) {
                    if (error) {
                        console.error('Failed to check Stripe status:', error);
                        setStripeConnected(false);
                        setStripeVerified(false);
                    } else {
                        setStripeConnected(!!data?.stripe_account_id);
                        setStripeVerified(!!(data?.stripe_verification_complete && data?.stripe_charges_enabled));
                    }
                }
            } finally {
                if (isMounted) {
                    setStripeCheckingStatus(false);
                }
            }
        }

        if (isOpen) {
            checkStripeStatus();
            if (invoice) {
                setCashReceived(Number(invoice.balance_due).toFixed(2));
            }
            if (initialMode) {
                setMode(initialMode);
            }
        }

        return () => { isMounted = false; };
    }, [isOpen, invoice?.id, invoice?.org_id, invoice?.balance_due, initialMode]);

    if (!isOpen || !invoice) return null;

    const accentColor = invoice.branding_snapshot.invoice_accent_color || DEFAULT_DOCUMENT_ACCENT;
    const balanceDue = Number(invoice.balance_due.toFixed(2));

    // Format currency
    const formatCurrency = (amount: number) =>
        new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount);

    // Handle Cash Payment
    const handleCashPayment = async () => {
        setStatus('processing');
        setError(null);

        try {
            const amount = parseFloat(cashReceived);
            if (isNaN(amount) || amount <= 0) {
                throw new Error('Please enter a valid amount');
            }
            if (amount > balanceDue) {
                throw new Error(`Amount exceeds balance due (${formatCurrency(balanceDue)})`);
            }

            // Round to 2 decimals
            const roundedAmount = Math.round(amount * 100) / 100;

            const { error: rpcError } = await supabase.rpc('fn_record_invoice_payment', {
                p_invoice_id: invoice.id,
                p_amount: roundedAmount,
                p_method: 'cash',
                p_notes: `Cash payment received`,
                p_stripe_payment_intent_id: null,
                p_stripe_fee: 0,
                p_platform_fee: 0,
                p_stripe_event_id: null
            });

            if (rpcError) throw rpcError;

            strike('land');
            setStatus('success');
            setTimeout(() => {
                onSuccess();
                resetAndClose();
            }, 1500);

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Payment failed');
            setStatus('error');
        }
    };

    // Handle Part Payment
    const handlePartPayment = async () => {
        setStatus('processing');
        setError(null);

        try {
            const amount = parseFloat(partAmount);
            if (isNaN(amount) || amount <= 0) {
                throw new Error('Please enter a valid amount');
            }
            if (amount > balanceDue) {
                throw new Error(`Amount exceeds balance due (${formatCurrency(balanceDue)})`);
            }

            // Round to 2 decimals
            const roundedAmount = Math.round(amount * 100) / 100;

            if (partMethod === 'cash') {
                const { error: rpcError } = await supabase.rpc('fn_record_invoice_payment', {
                    p_invoice_id: invoice.id,
                    p_amount: roundedAmount,
                    p_method: 'cash',
                    p_notes: `Part payment (cash)`,
                    p_stripe_payment_intent_id: null,
                    p_stripe_fee: 0,
                    p_platform_fee: 0,
                    p_stripe_event_id: null
                });

                if (rpcError) throw rpcError;

                strike('land');
                setPromptEmailRemaining(true);
            } else {
                // Card payment - trigger Stripe flow
                await handleCardPayment(roundedAmount, 'redirect'); // default to redirect for part payment for now
            }

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Payment failed');
            setStatus('error');
        }
    };

    const handleEmailRemaining = async () => {
        if (!invoice) return;
        setEmailSending(true);
        setError(null);
        try {
            const amount = parseFloat(partAmount);
            const remaining = balanceDue - amount;

            // Token-gated payment link (audit finding F-08).
            //
            // This used to be `${origin}/pay/invoice/${invoice.id}` — a "stable
            // link that never expires", which is exactly the problem: the
            // invoice UUID was a bearer credential with no expiry and no
            // revocation, so anyone who ever saw the URL kept access to the
            // customer's invoice forever. That route no longer exists.
            //
            // fn_issue_invoice_portal_token reuses a live token when one exists,
            // so re-sending an invoice does not invalidate the link the customer
            // already has.
            const origin = window.location.origin;
            const { data: portalToken, error: tokenError } = await supabase.rpc(
                'fn_issue_invoice_portal_token',
                { p_invoice_id: invoice.id },
            );
            if (tokenError || !portalToken) {
                throw new Error('Could not create a secure payment link. Please try again.');
            }
            const paymentLink = `${origin}/pay/t/${portalToken}`;

            // Fetch Customer Details and Org ID.
            //
            // `email` is deliberately NOT embedded as `customers(email)` — that
            // reads the plaintext column, which is encrypted at rest
            // (20260810211000) and emptied by the purge script. Embedding it
            // would leave this throwing "Could not find customer email address"
            // for every customer who has one.
            const { data: customerData, error: customerError } = await supabase
                .from('invoices')
                .select('org_id, customer_id, customers(full_name)')
                .eq('id', invoice.id)
                .single();

            // Supabase joins return an array when the FK isn't unique-by-default.
            // Normalize to a single object so the rest of the code can use it directly.
            const rawCustomer = customerData?.customers as
                | { full_name: string }
                | { full_name: string }[]
                | null
                | undefined;
            const joined = Array.isArray(rawCustomer) ? (rawCustomer[0] ?? null) : (rawCustomer ?? null);
            const orgId = customerData?.org_id;

            const { data: contact } = customerData?.customer_id
                ? await supabase
                    .from('customers_decrypted')
                    .select('email')
                    .eq('id', customerData.customer_id)
                    .maybeSingle()
                : { data: null };

            const customer = joined
                ? { full_name: joined.full_name, email: contact?.email ?? '' }
                : null;

            if (customerError || !customer?.email) {
                throw new Error('Could not find customer email address.');
            }

            // Send Email
            const { error: emailError } = await supabase.functions.invoke('send-email', {
                body: {
                    type: 'invoice_email',
                    data: {
                        invoice_id: invoice.id,
                        org_id: orgId,
                        email: customer.email,
                        name: customer.full_name,
                        invoice_number: invoice.invoice_number,
                        amount_due: remaining,
                        payment_link_url: paymentLink,
                        subject: `Remaining Balance for Invoice ${invoice.invoice_number}`,
                        message: `Hi ${customer.full_name},\n\nThank you for your initial payment. Here is the link to pay the remaining balance of £${remaining.toFixed(2)} on Invoice ${invoice.invoice_number}.\n\nSecure payment link:\n${paymentLink}`
                    }
                }
            });

            if (emailError) throw emailError;

            // Success Transition
            setStatus('success');
            setPromptEmailRemaining(false);
            setTimeout(() => {
                onSuccess();
                resetAndClose();
            }, 1500);

        } catch (err) {
            console.error('Email error:', err);
            setError(err instanceof Error ? err.message : 'Failed to send remaining balance email');
        } finally {
            setEmailSending(false);
        }
    };

    // Handle Card Payment (via Stripe Edge Function)
    const handleCardPayment = async (amount?: number, action: 'redirect' | 'qr' = 'redirect') => {
        setStatus('processing');
        setError(null);

        const paymentAmount = amount ?? balanceDue;

        try {
            // Call edge function to create payment intent
            const { data, error: fnError } = await supabase.functions.invoke('create-invoice-checkout', {
                body: {
                    invoice_id: invoice.id,
                    amount: paymentAmount, // Pass in pounds, backend converts to cents
                    success_url: window.location.href
                }
            });

            if (fnError) {
                // supabase-js wraps a non-2xx as FunctionsHttpError whose .message is a
                // generic "non-2xx status code". The real reason (e.g. "invoice not
                // accepting payments", or a Stripe error) is in the response body on
                // .context — surface it so the user sees WHY instead of "no 2xx", and
                // can just retry inline rather than closing and reopening the modal.
                let realMessage = fnError.message;
                const ctx = (fnError as { context?: Response }).context;
                if (ctx && typeof ctx.json === 'function') {
                    try {
                        const body = await ctx.json();
                        if (body?.error) realMessage = body.error;
                    } catch {
                        // body wasn't JSON — keep the generic message
                    }
                }
                throw new Error(realMessage);
            }

            if (data?.checkout_url) {
                if (action === 'redirect') {
                    // Redirect to Stripe Checkout
                    window.location.href = data.checkout_url;
                } else {
                    // Show QR Code
                    setCheckoutUrl(data.checkout_url);
                    setStatus('idle');
                }
            } else if (data?.client_secret) {
                // TODO: Integrate Stripe Elements for inline payment
                // For now, show a message
                setError('Inline card payment coming soon. Please use cash or contact support.');
                setStatus('error');
            } else {
                throw new Error('Invalid response from payment service');
            }

        } catch (err) {
            setError(err instanceof Error ? err.message : 'Card payment failed');
            setStatus('error');
        }
    };

    const resetAndClose = () => {
        setMode('card');
        setStatus('idle');
        setError(null);
        setPartAmount('');
        setPartMethod('cash');
        setCashReceived('');
        setCheckoutUrl(null);
        setPromptEmailRemaining(false);
        onClose();
    };

    // Prompt Email state
    if (promptEmailRemaining) {
        const remaining = balanceDue - parseFloat(partAmount || '0');
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-scrim/80 backdrop-blur-sm">
                <div className="bg-panel rounded-xl p-8 max-w-sm w-full text-center border border-line shadow-2xl animate-in fade-in zoom-in-95">
                    <div className="w-16 h-16 bg-info/10 rounded-full mx-auto flex items-center justify-center mb-4">
                        <CheckCircle className="w-8 h-8 text-info" />
                    </div>
                    <h3 className="text-xl font-bold text-fg mb-2">Part Payment Saved</h3>
                    <p className="text-fg-3 mb-6 text-sm">
                        There is a remaining balance of <strong className="text-fg">£{remaining.toFixed(2)}</strong>.
                        Would you like to email a secure checkout link for this remaining amount to the customer?
                    </p>

                    {error && (
                        <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg text-danger text-sm text-center">
                            {error}
                        </div>
                    )}

                    <div className="flex flex-col gap-3 mt-4">
                        <button
                            onClick={handleEmailRemaining}
                            disabled={emailSending}
                            className="w-full py-3 px-4 hover:opacity-90 text-on-accent font-bold rounded-lg transition-opacity flex items-center justify-center gap-2"
                            style={{ backgroundColor: accentColor }}
                        >
                            {emailSending ? <Loader2 className="w-5 h-5 animate-spin" /> : 'Yes, Email Link'}
                        </button>
                        <button
                            onClick={() => {
                                setStatus('success');
                                setPromptEmailRemaining(false);
                                setTimeout(() => {
                                    onSuccess();
                                    resetAndClose();
                                }, 1500);
                            }}
                            disabled={emailSending}
                            className="w-full py-3 px-4 bg-tint/5 hover:bg-tint/10 text-fg font-bold rounded-lg transition-colors border border-tint/10"
                        >
                            No, Thanks
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // Success state
    if (status === 'success') {
        return (
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-scrim/70 backdrop-blur-sm">
                <div className="bg-panel rounded-xl p-8 w-full max-w-md text-center animate-in zoom-in-95">
                    <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-success/20 flex items-center justify-center">
                        <CheckCircle className="w-10 h-10 text-success" />
                    </div>
                    <h2 className="text-xl font-bold text-fg mb-2">Payment Recorded!</h2>
                    <p className="text-fg-3 text-sm">The invoice has been updated.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-scrim/70 backdrop-blur-sm p-4">
            <div className="bg-panel border border-tint/10 rounded-xl w-full max-w-lg overflow-hidden animate-in zoom-in-95">
                {/* Header */}
                <div
                    className="px-6 py-4 flex items-center justify-between"
                    style={{ backgroundColor: accentColor }}
                >
                    <div>
                        <h2 className="text-lg font-bold text-on-fill">Collect Payment</h2>
                        <p className="text-sm text-on-fill/80">{invoice.invoice_number}</p>
                    </div>
                    <button
                        onClick={resetAndClose}
                        className="p-2 text-on-fill/70 hover:text-on-fill hover:bg-on-fill/10 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Balance Due */}
                <div className="px-6 py-4 bg-tint/5 border-b border-tint/10">
                    <p className="text-sm text-fg-3">Balance Due</p>
                    <p className="text-3xl font-bold text-fg font-mono tabular-nums">
                        {formatCurrency(balanceDue)}
                    </p>
                </div>

                {/* Mode Tabs */}
                <div className="flex border-b border-tint/10">
                    {[
                        { id: 'card' as const, icon: CreditCard, label: 'Card' },
                        { id: 'cash' as const, icon: Banknote, label: 'Cash' },
                        { id: 'part' as const, icon: Split, label: 'Part Pay' },
                    ].map((tab) => (
                        <button
                            key={tab.id}
                            onClick={() => { setMode(tab.id); setError(null); setCheckoutUrl(null); }}
                            className={`flex-1 flex items-center justify-center gap-2 py-3 text-sm font-bold transition-all ${mode === tab.id
                                ? 'text-fg bg-tint/10 border-b-2'
                                : 'text-fg-4 hover:text-fg-2'
                                }`}
                            style={{ borderColor: mode === tab.id ? accentColor : 'transparent' }}
                        >
                            <tab.icon className="w-4 h-4" />
                            {tab.label}
                        </button>
                    ))}
                </div>

                {/* Content Area */}
                <div className="p-6">
                    {/* Error */}
                    {error && (
                        <div className="mb-4 p-3 bg-danger/10 border border-danger/20 rounded-lg flex items-center gap-2 text-danger text-sm">
                            <AlertCircle className="w-4 h-4 flex-shrink-0" />
                            {error}
                        </div>
                    )}

                    {/* Card Mode */}
                    {mode === 'card' && (
                        <div className="space-y-4">
                            {/* Stripe Not Connected Warning */}
                            {!stripeCheckingStatus && !stripeConnected && (
                                <div className="p-4 rounded-lg bg-danger/10 border border-danger/30 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-danger mb-2">Connect Stripe to Accept Card Payments</p>
                                        <p className="text-xs text-danger mb-3">
                                            Card payments are not available because your Stripe account is not connected.
                                        </p>
                                        <a 
                                            href={`${getAppUrl()}/settings/billing`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs font-semibold text-danger hover:text-danger px-3 py-1.5 bg-danger/20 rounded hover:bg-danger/30 transition-colors inline-block"
                                        >
                                            Connect Stripe Now →
                                        </a>
                                    </div>
                                </div>
                            )}

                            {/* Stripe Verification Warning */}
                            {!stripeCheckingStatus && stripeConnected && !stripeVerified && (
                                <div className="p-4 rounded-lg bg-gold-fill/10 border border-gold/30 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gold mb-2">Waiting for Stripe Verification</p>
                                        <p className="text-xs text-gold mb-3">
                                            Your Stripe account is connected but hasn't completed ID verification and KYC checks yet. Card payments will be available once your account is verified.
                                        </p>
                                        <a 
                                            href={`${getAppUrl()}/settings/billing`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-xs text-gold hover:text-gold inline-block underline"
                                        >
                                            Check Verification Status →
                                        </a>
                                    </div>
                                </div>
                            )}

                            {!checkoutUrl ? (
                                <>
                                    <p className="text-fg-3 text-sm">
                                        Collect full payment of {formatCurrency(balanceDue)} via Stripe.
                                    </p>

                                    {/* Fee Transparency */}
                                    <div className="p-4 bg-card/50 border border-tint/5 rounded-lg space-y-2 text-sm">
                                        <div className="flex justify-between text-fg-3">
                                            <span>Stripe Processing (1.5% + 20p)</span>
                                            <span className="text-danger">-{formatCurrency(balanceDue * 0.015 + 0.20)}</span>
                                        </div>
                                        <div className="flex justify-between text-fg-3">
                                            <span>ForgeDash Platform (0.5%)</span>
                                            <span className="text-danger">-{formatCurrency(balanceDue * 0.005)}</span>
                                        </div>
                                        <div className="pt-2 border-t border-tint/5 flex justify-between font-medium text-fg">
                                            <span>Estimated Payout</span>
                                            <span className="text-success">{formatCurrency(balanceDue - (balanceDue * 0.015 + 0.20) - (balanceDue * 0.005))}</span>
                                        </div>
                                    </div>

                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4">
                                        <button
                                            onClick={() => handleCardPayment(balanceDue, 'redirect')}
                                            disabled={status === 'processing' || !stripeConnected || !stripeVerified}
                                            className="w-full py-4 rounded-lg font-bold text-on-fill flex flex-col items-center justify-center gap-2 transition-all hover:brightness-110 disabled:opacity-50 border border-tint/10 shadow-sm"
                                            style={{ backgroundColor: accentColor }}
                                        >
                                            <CreditCard className="w-6 h-6" />
                                            <span className="text-sm">Pay on this device</span>
                                        </button>
                                        <button
                                            onClick={() => handleCardPayment(balanceDue, 'qr')}
                                            disabled={status === 'processing' || !stripeConnected || !stripeVerified}
                                            className="w-full py-4 bg-raised hover:bg-tint/5 rounded-lg border border-tint/10 font-bold text-fg flex flex-col items-center justify-center gap-2 transition-all disabled:opacity-50 shadow-sm"
                                        >
                                            <ScanLine className="w-6 h-6 text-fg-3" />
                                            <span className="text-sm">Customer Device (QR)</span>
                                        </button>
                                    </div>

                                    {status === 'processing' && (
                                        <div className="flex justify-center py-4">
                                            <Loader2 className="w-6 h-6 animate-spin text-fg-4" />
                                        </div>
                                    )}
                                </>
                            ) : (
                                <div className="flex flex-col items-center space-y-6 py-6 animate-in fade-in zoom-in-95">
                                    <div className="text-center space-y-1">
                                        <h3 className="text-xl font-bold text-fg">Scan to Pay</h3>
                                        <p className="text-sm text-fg-3">Ask the customer to scan this code with their phone camera to pay securely via Stripe.</p>
                                    </div>

                                    <div data-theme="paper" className="bg-card p-6 rounded-2xl shadow-lifted">
                                        <QRCode value={checkoutUrl} size={220} />
                                    </div>

                                    <div className="flex flex-col items-center gap-3 w-full">
                                        <button
                                            onClick={() => window.location.href = checkoutUrl}
                                            className="text-sm font-medium transition-colors hover:underline"
                                            style={{ color: accentColor }}
                                        >
                                            Open link on this device instead
                                        </button>
                                        <button
                                            onClick={() => setCheckoutUrl(null)}
                                            className="text-fg-4 text-sm hover:text-fg transition-colors"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Cash Mode */}
                    {mode === 'cash' && (
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm text-fg-3 mb-2">Amount Received (£)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={balanceDue}
                                    value={cashReceived}
                                    onChange={(e) => setCashReceived(e.target.value)}
                                    placeholder={balanceDue.toFixed(2)}
                                    className="w-full bg-raised border border-tint/10 rounded-lg px-4 py-3 text-fg font-mono text-lg focus:outline-none focus:border-accent/50"
                                />
                            </div>

                            {/* Change calculation */}
                            {cashReceived && parseFloat(cashReceived) > balanceDue && (
                                <div className="p-3 bg-gold-fill/10 border border-gold/20 rounded-lg">
                                    <p className="text-gold text-sm">
                                        Change: {formatCurrency(parseFloat(cashReceived) - balanceDue)}
                                    </p>
                                </div>
                            )}

                            <button
                                onClick={handleCashPayment}
                                disabled={status === 'processing' || !cashReceived}
                                className="fd-btn fd-btn-brass fd-btn-lg w-full"
                            >
                                {status === 'processing' ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Banknote className="w-5 h-5" />
                                        Record Cash Payment
                                    </>
                                )}
                            </button>
                        </div>
                    )}

                    {/* Part Payment Mode */}
                    {mode === 'part' && (
                        <div className="space-y-4">
                            {/* Stripe Not Connected Warning */}
                            {!stripeCheckingStatus && !stripeConnected && partMethod === 'card' && (
                                <div className="p-4 rounded-lg bg-danger/10 border border-danger/30 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-danger flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-danger mb-2">Connect Stripe for Card Payments</p>
                                        <p className="text-xs text-danger">
                                            To accept card payments for this part payment, you need to connect your Stripe account first.
                                        </p>
                                    </div>
                                </div>
                            )}

                            {/* Stripe Verification Warning */}
                            {!stripeCheckingStatus && stripeConnected && !stripeVerified && partMethod === 'card' && (
                                <div className="p-4 rounded-lg bg-gold-fill/10 border border-gold/30 flex items-start gap-3">
                                    <AlertCircle className="w-5 h-5 text-gold flex-shrink-0 mt-0.5" />
                                    <div className="flex-1">
                                        <p className="text-sm font-semibold text-gold mb-2">Waiting for Stripe Verification</p>
                                        <p className="text-xs text-gold">
                                            Card payments will be available once your Stripe account completes verification.
                                        </p>
                                    </div>
                                </div>
                            )}

                            <div>
                                <label className="block text-sm text-fg-3 mb-2">Deposit / Part Payment Amount (£)</label>
                                <input
                                    type="number"
                                    step="0.01"
                                    min="0.01"
                                    max={balanceDue}
                                    value={partAmount}
                                    onChange={(e) => setPartAmount(e.target.value)}
                                    placeholder="e.g., 100.00"
                                    className="w-full bg-raised border border-tint/10 rounded-lg px-4 py-3 text-fg font-mono text-lg focus:outline-none focus:border-accent/50"
                                />
                            </div>

                            <div>
                                <label className="block text-sm text-fg-3 mb-2">Payment Method</label>
                                <div className="flex gap-2">
                                    <button
                                        type="button"
                                        onClick={() => setPartMethod('cash')}
                                        className={`flex-1 py-2 rounded-lg border font-bold text-sm transition-all ${partMethod === 'cash'
                                            ? 'bg-success/20 border-success text-success'
                                            : 'bg-tint/5 border-tint/10 text-fg-3 hover:text-fg'
                                            }`}
                                    >
                                        <Banknote className="w-4 h-4 inline mr-2" />
                                        Cash
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setPartMethod('card')}
                                        className={`flex-1 py-2 rounded-lg border font-bold text-sm transition-all ${partMethod === 'card'
                                            ? 'border-info text-info'
                                            : 'bg-tint/5 border-tint/10 text-fg-3 hover:text-fg'
                                            }`}
                                        style={partMethod === 'card' ? { backgroundColor: `${accentColor}20` } : {}}
                                    >
                                        <CreditCard className="w-4 h-4 inline mr-2" />
                                        Card
                                    </button>
                                </div>
                            </div>

                            {/* Remaining balance preview */}
                            {partAmount && parseFloat(partAmount) > 0 && parseFloat(partAmount) <= balanceDue && (
                                <div className="space-y-2">
                                    <div className="p-3 bg-tint/5 border border-tint/10 rounded-lg">
                                        <div className="flex justify-between text-sm">
                                            <span className="text-fg-3">This payment:</span>
                                            <span className="text-fg font-mono">{formatCurrency(parseFloat(partAmount))}</span>
                                        </div>
                                        <div className="flex justify-between text-sm mt-1">
                                            <span className="text-fg-3">Remaining after:</span>
                                            <span className="text-gold font-mono">
                                                {formatCurrency(balanceDue - parseFloat(partAmount))}
                                            </span>
                                        </div>
                                    </div>

                                    {/* Fee Transparency for Card Part Payments */}
                                    {partMethod === 'card' && (
                                        <div className="p-3 bg-card/50 border border-tint/5 rounded-lg space-y-1 text-xs">
                                            <div className="flex justify-between text-fg-3">
                                                <span>Stripe Processing (1.5% + 20p)</span>
                                                <span className="text-danger">-{formatCurrency(parseFloat(partAmount) * 0.015 + 0.20)}</span>
                                            </div>
                                            <div className="flex justify-between text-fg-3">
                                                <span>ForgeDash Platform (0.5%)</span>
                                                <span className="text-danger">-{formatCurrency(parseFloat(partAmount) * 0.005)}</span>
                                            </div>
                                            <div className="pt-1 mt-1 border-t border-tint/5 flex justify-between font-medium text-fg">
                                                <span>Estimated Payout</span>
                                                <span className="text-success">{formatCurrency(parseFloat(partAmount) - (parseFloat(partAmount) * 0.015 + 0.20) - (parseFloat(partAmount) * 0.005))}</span>
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            <button
                                onClick={handlePartPayment}
                                disabled={status === 'processing' || !partAmount || parseFloat(partAmount) <= 0 || (partMethod === 'card' && (!stripeConnected || !stripeVerified))}
                                className="fd-btn fd-btn-brass fd-btn-lg w-full"
                            >
                                {status === 'processing' ? (
                                    <Loader2 className="w-5 h-5 animate-spin" />
                                ) : (
                                    <>
                                        <Split className="w-5 h-5" />
                                        Record Part Payment
                                    </>
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
